import {FC, useRef, useState} from 'react';

import {Box, Card, Checkbox, Skeleton, Stack, styled, useMediaQuery} from '@mui/material';
import {t} from 'i18next';

import newEmptyBasketImage from 'assets/images/new-empty-basket.png';
import productImage from 'assets/images/product-image.png';
import {config} from 'config/env';
import theme from 'config/theme';
import {isEnabledRecommendationMFE} from 'constants/recommendation';
import {useAppSelector} from 'hooks';
import {useAppMode} from 'hooks/useAppMode';
import ReactNative from 'services/react-native';
import {UserAddress} from 'types';
import {hasItemsInBasket} from 'utils';
import {isUnifiedBasketEnabled} from 'utils/helper';
import {BasketEmptyBodyView} from 'views/basket-empty-body/BasketEmptyBody';
import {BasketWeightCapacityStickyComponent} from 'views/basket-express-weight-limit/BasketWeightCapacitySticky.component';
import {ProductList} from 'views/basket-product-list/ProductList';
import {BasketRecommendations} from 'views/basket-recommendations/BasketRecommendations';
import {RecommendationsCarouselMFE} from 'views/basket-recommendations/RecommendationsCarouselMFE';
import BasketSummary from 'views/basket-summary/BasketSummary';
import {DeliveryTypeSelector} from 'views/delivery-type-selector/DeliveryTypeSelector';
import ShippingAddress from 'views/shipping-address/ShippingAddress';

import {StyledBodyWrapper} from './styles';

const StyledSectionWrap = styled(Box)(({theme}) => ({
  display: 'flex',
  width: '100%',
  flexDirection: 'row',
  position: 'relative',
  margin: '20px',
  maxWidth: '-webkit-fill-available',
  borderRadius: '12px',
  backgroundColor: theme.palette.common.white,
  '&.multiple': {
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(2)
    }
  }
}));

const StyledSectionContentContainer = styled(Box)(() => ({
  display: 'flex',
  width: '100%',
  flexDirection: 'column',
  position: 'relative'
}));

export const StyledSectionHeader = styled(Box)(() => ({
  width: 'auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  borderRadius: '12px 12px 0px 0px',
  background: '#fff',
  gap: '4px',
  borderBottom: '1px solid #E8E8E8',
  padding: '16px 16px 16px 16px'
}));

export const StyledCard1 = styled(Card)(() => ({
  display: 'flex',
  flexDirection: 'column',
  boxShadow: 'none',
  padding: '16px 16px 16px 16px',
  borderBottom: '1px solid #E8E8E8'
}));

export const StyledCard2 = styled(Card)(() => ({
  display: 'flex',
  flexDirection: 'column',
  boxShadow: 'none',
  padding: '16px 16px 16px 16px',
  borderBottom: '1px solid #E8E8E8',
  borderRadius: '12px'
}));

export const StyledBasketProductImage = styled(Box)(() => ({
  width: 64,
  height: 64,
  overflow: 'hidden',
  borderRadius: '4px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}));

export type BodyProps = {
  loading: boolean;
  address: UserAddress;
  onClick: () => void;
  isLoading: boolean;
};
export const Body: FC<BodyProps> = ({loading, address, isLoading, onClick}) => {
  const {isExpress} = useAppMode();
  const {basket} = useAppSelector((state) => state?.basket);
  const {recommendations} = useAppSelector((state) => state.recommendations);
  const {idToken} = useAppSelector((state) => state?.auth) || {};
  const isReactNativeWebView = ReactNative.isReactNativeWebView();
  const isMobileView = useMediaQuery(theme.breakpoints.only('mobile'));
  const [isScrollDown, setIsScrollDown] = useState(false);

  const isUnifiedBasketEnabledFlag = isUnifiedBasketEnabled();

  const isStoreInActive = useAppSelector((state) => state?.app.isStoreInActive);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const lastScrollY = useRef(0);

  const handleScroll = () => {
    if (!containerRef.current) return;

    const currentScrollY = containerRef.current.scrollTop || 0;
    const maxScrollY = containerRef.current.scrollHeight - containerRef.current.clientHeight;

    if (currentScrollY >= maxScrollY - 1) return;

    setIsScrollDown(currentScrollY > 1 && currentScrollY > lastScrollY.current);
    lastScrollY.current = currentScrollY;
  };

  const isBasketRecommendationsEnabled = recommendations?.products?.length > 0 && hasItemsInBasket(basket);
  return (
    <>
      {(isReactNativeWebView || isMobileView) && idToken && (!isScrollDown || config.ENABLE_UNIFY_BASKET) && (
        <ShippingAddress address={address as UserAddress} isLoading={isLoading || loading} onClick={onClick} />
      )}
      {isUnifiedBasketEnabledFlag && (
        <DeliveryTypeSelector isLoading={isLoading || loading} miniVersion={isScrollDown} openAddressModal={onClick} />
      )}
      <StyledBodyWrapper onScroll={handleScroll} loading={loading} ref={containerRef}>
        {loading ? (
          <>
            {[1, 2].map((i) => (
              <StyledSectionWrap className="multiple" key={i}>
                <StyledSectionContentContainer>
                  <StyledSectionHeader>
                    <Box display="flex" flexDirection="row" width="100%" alignItems="center" gap="8px">
                      <Checkbox
                        name="isDefault"
                        disabled={true}
                        sx={{
                          color: '#C7C7C7',
                          '&.Mui-checked': {
                            color: 'primary'
                          },
                          height: 'fit-content',
                          padding: 0
                        }}
                      />
                      <Stack height={20} spacing={1} width="20%">
                        <Skeleton variant="rectangular" height="100%" sx={{borderRadius: '2px'}} />
                      </Stack>
                    </Box>
                    <Stack height={20} spacing={1} width="35%">
                      <Skeleton variant="rectangular" height="100%" sx={{borderRadius: '2px'}} />
                    </Stack>
                  </StyledSectionHeader>
                  <StyledCard1>
                    <Box display="flex" gap="10px">
                      <Checkbox
                        name="isDefault"
                        disabled={true}
                        sx={{
                          color: '#C7C7C7',
                          '&.Mui-checked': {
                            color: 'primary'
                          },
                          height: 'fit-content',
                          padding: 0
                        }}
                      />
                      <StyledBasketProductImage>
                        <img src={productImage} alt="" />
                      </StyledBasketProductImage>
                      <Box width="100%" display="flex" flexDirection="column" gap="8px">
                        <Stack height={20} spacing={1} width="40%">
                          <Skeleton variant="rectangular" height="100%" sx={{borderRadius: '2px'}} />
                        </Stack>
                        <Stack height={16} spacing={1} width="30%">
                          <Skeleton variant="rectangular" height="100%" sx={{borderRadius: '2px'}} />
                        </Stack>
                      </Box>
                    </Box>
                    <Box
                      width="100%"
                      display="flex"
                      flexDirection="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Stack height={20} spacing={1} width="30%">
                        <Skeleton variant="rectangular" height="100%" sx={{borderRadius: '2px'}} />
                      </Stack>
                      <Stack height={32} spacing={1} width="30%">
                        <Skeleton variant="rectangular" height="100%" sx={{borderRadius: 4}} />
                      </Stack>
                    </Box>
                  </StyledCard1>
                  <StyledCard2>
                    <Box display="flex" gap="10px">
                      <Checkbox
                        name="isDefault"
                        disabled={true}
                        sx={{
                          color: '#C7C7C7',
                          '&.Mui-checked': {
                            color: 'primary'
                          },
                          height: 'fit-content',
                          padding: 0
                        }}
                      />
                      <StyledBasketProductImage>
                        <img src={productImage} alt="" />
                      </StyledBasketProductImage>
                      <Box width="100%" display="flex" flexDirection="column" gap="8px">
                        <Stack height={20} spacing={1} width="40%">
                          <Skeleton variant="rectangular" height="100%" sx={{borderRadius: '2px'}} />
                        </Stack>
                        <Stack height={16} spacing={1} width="30%">
                          <Skeleton variant="rectangular" height="100%" sx={{borderRadius: '2px'}} />
                        </Stack>
                      </Box>
                    </Box>
                    <Box
                      width="100%"
                      display="flex"
                      flexDirection="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Stack height={20} spacing={1} width="30%">
                        <Skeleton variant="rectangular" height="100%" sx={{borderRadius: '2px'}} />
                      </Stack>
                      <Stack height={32} spacing={1} width="30%">
                        <Skeleton variant="rectangular" height="100%" sx={{borderRadius: 4}} />
                      </Stack>
                    </Box>
                  </StyledCard2>
                </StyledSectionContentContainer>
              </StyledSectionWrap>
            ))}
          </>
        ) : (
          <>
            {isStoreInActive ? (
              <BasketEmptyBodyView
                title={t('message.areaServiceUnavailable')}
                description={t('message.letsChange')}
                loading={false}
                mediaURL={newEmptyBasketImage}
                hideButton={true}
              />
            ) : (
              <>
                <ProductList loading={loading} />
                <BasketSummary loading={loading} />
                {isEnabledRecommendationMFE && hasItemsInBasket(basket) ? (
                  <RecommendationsCarouselMFE />
                ) : isBasketRecommendationsEnabled ? (
                  <BasketRecommendations />
                ) : null}
              </>
            )}
          </>
        )}
        {!loading && isExpress && hasItemsInBasket(basket) && <BasketWeightCapacityStickyComponent />}
      </StyledBodyWrapper>
    </>
  );
};

Body.displayName = 'Body';
export default Body;
